import React, { useMemo, useState } from "react";
import { ArrowRight, Mail } from "lucide-react";
import { CALENDLY_URL } from "../../i18n";
import { useLang } from "../../context/LangContext";
import { toolsApi } from "../../api";
import { toast } from "sonner";

const amortize = (loan, ratePct, years = 30) => {
  const mr = ratePct / 100 / 12;
  const n = years * 12;
  if (mr <= 0) return loan / n;
  return (loan * mr * Math.pow(1 + mr, n)) / (Math.pow(1 + mr, n) - 1);
};

const Field = ({ label, value, onChange }) => (
  <div>
    <label style={{ display: "block", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "0.5rem" }}>{label}</label>
    <input className="field" type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} />
  </div>
);

export default function Calculator() {
  const { c } = useLang();
  const t = c.calc;
  const [mode, setMode] = useState("purchase");

  React.useEffect(() => {
    const handler = (e) => { if (e.detail) setMode(e.detail); };
    window.addEventListener("dscr-calc-mode", handler);
    return () => window.removeEventListener("dscr-calc-mode", handler);
  }, []);

  // Purchase
  const [price, setPrice] = useState(300000);
  const [rent, setRent] = useState(2500);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(7.5);
  const [taxIns, setTaxIns] = useState(463);
  // Cash-out refi
  const [value, setValue] = useState(400000);
  const [curBal, setCurBal] = useState(180000);
  const [ltv, setLtv] = useState(75);
  // Second lien
  const [firstBal, setFirstBal] = useState(200000);
  const [firstPay, setFirstPay] = useState(1250);
  const [cltv, setCltv] = useState(80);
  const [secondRate, setSecondRate] = useState(9.5);

  const r = useMemo(() => {
    if (mode === "purchase") {
      const loan = price * (1 - down / 100);
      const pi = amortize(loan, rate);
      const pitia = pi + Number(taxIns);
      return { dscr: pitia > 0 ? rent / pitia : 0, cashflow: rent - pitia, rows: [[t.out.loan, loan], [t.out.pi, pi], [t.out.pitia, pitia]] };
    }
    if (mode === "cashout") {
      const newLoan = value * (ltv / 100);
      const cashOut = Math.max(0, newLoan - curBal);
      const pi = amortize(newLoan, rate);
      const pitia = pi + Number(taxIns);
      return { dscr: pitia > 0 ? rent / pitia : 0, cashflow: rent - pitia, rows: [[t.out.newLoan, newLoan], [t.out.cashOut, cashOut], [t.out.pi, pi], [t.out.pitia, pitia]], highlight: [t.out.cashOut, cashOut] };
    }
    // second lien
    const maxCombined = value * (cltv / 100);
    const secondLoan = Math.max(0, maxCombined - firstBal);
    const secondPi = amortize(secondLoan, secondRate);
    const combined = Number(firstPay) + secondPi + Number(taxIns);
    return { dscr: combined > 0 ? rent / combined : 0, cashflow: rent - combined, rows: [[t.out.equity, secondLoan], [t.out.secondPi, secondPi], [t.out.combined, combined]], highlight: [t.out.equity, secondLoan], note: t.out.firstKept };
  }, [mode, price, rent, down, rate, taxIns, value, curBal, ltv, firstBal, firstPay, cltv, secondRate, t]);

  const qualifies = r.dscr >= 1.0;
  const fmt = (n) => "$" + Math.round(n).toLocaleString();

  const modeLabels = { purchase: "Purchase", cashout: "Cash-Out Refi", secondlien: "Second Lien / HELOC" };
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const emailResults = async () => {
    if (!email || !email.includes("@")) { toast.error(t.email?.invalid || "Enter a valid email"); return; }
    setSending(true);
    try {
      await toolsApi.calcEmail({
        email, mode: modeLabels[mode],
        dscr: Number(r.dscr.toFixed(2)), cashflow: Math.round(r.cashflow),
        rows: r.rows.map(([label, value]) => ({ label, value: Math.round(value) })),
      });
      toast.success(t.email?.sent || "Your DSCR numbers are on the way!");
      setEmail("");
    } catch { toast.error(t.email?.failed || "Could not send. Try again."); }
    finally { setSending(false); }
  };

  const inputs = {
    purchase: [[t.f.price, price, setPrice], [t.f.rent, rent, setRent], [t.f.down, down, setDown], [t.f.rate, rate, setRate], [t.f.taxIns, taxIns, setTaxIns]],
    cashout: [[t.f.value, value, setValue], [t.f.curBal, curBal, setCurBal], [t.f.ltv, ltv, setLtv], [t.f.rate, rate, setRate], [t.f.rent, rent, setRent], [t.f.taxIns, taxIns, setTaxIns]],
    secondlien: [[t.f.value, value, setValue], [t.f.firstBal, firstBal, setFirstBal], [t.f.firstPay, firstPay, setFirstPay], [t.f.cltv, cltv, setCltv], [t.f.secondRate, secondRate, setSecondRate], [t.f.rent, rent, setRent], [t.f.taxIns, taxIns, setTaxIns]],
  };

  return (
    <section id="calculator" className="section">
      <div className="container-x">
        <div className="reveal" style={{ maxWidth: 720, marginBottom: "2.2rem" }}>
          <span className="eyebrow">{t.eyebrow}</span>
          <h2 className="display" style={{ fontSize: "clamp(2.2rem,5vw,4rem)", margin: "1.2rem 0" }}>
            {t.title.pre}<span className="italic-gold">{t.title.em}</span>{t.title.post}
          </h2>
          <p className="body-text">{t.para}</p>
        </div>

        <div style={{ display: "inline-flex", gap: "0.4rem", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 999, padding: "0.35rem", marginBottom: "2rem", flexWrap: "wrap" }}>
          {["purchase", "cashout", "secondlien"].map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{ padding: "0.55rem 1.4rem", borderRadius: 999, border: "none", cursor: "pointer", fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", transition: "all 0.25s ease", background: mode === m ? "var(--gold)" : "transparent", color: mode === m ? "#17130a" : "var(--muted)", fontWeight: mode === m ? 500 : 400 }}>{t.modes[m]}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
          <div className="card" style={{ padding: "2.5rem" }}>
            <div style={{ display: "grid", gap: "1.3rem" }}>
              {inputs[mode].map(([label, val, setter]) => (
                <Field key={label} label={label} value={val} onChange={setter} />
              ))}
            </div>
            {mode === "purchase" && <p style={{ fontSize: "0.75rem", color: "var(--muted-2)", marginTop: "1.4rem", lineHeight: 1.6 }}>{t.hint}</p>}
          </div>

          <div className="card" style={{ padding: "2.5rem", background: "linear-gradient(180deg, rgba(201,162,75,0.06), transparent)", display: "flex", flexDirection: "column" }}>
            <div style={{ textAlign: "center", marginBottom: "1.8rem" }}>
              <div className="eyebrow" style={{ color: "var(--muted-2)" }}>{t.out.dscr}</div>
              <div className="font-serif" style={{ fontSize: "4.5rem", lineHeight: 1, color: qualifies ? "var(--green)" : "var(--red)" }}>{r.dscr.toFixed(2)}</div>
              <div style={{ fontSize: "0.85rem", color: qualifies ? "var(--green)" : "var(--red)", marginTop: "0.5rem" }}>{qualifies ? t.out.qualifies : t.out.belowP}</div>
            </div>
            <div style={{ display: "grid", gap: "0.9rem", marginBottom: "1.4rem" }}>
              {r.rows.map(([l, v], i) => {
                const isHl = r.highlight && r.highlight[0] === l;
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingBottom: "0.7rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <span style={{ color: isHl ? "var(--gold)" : "var(--muted)", fontSize: "0.85rem" }}>{l}</span>
                    <span className="font-serif" style={{ fontSize: "1.2rem", color: isHl ? "var(--gold)" : "var(--text)" }}>{fmt(v)}</span>
                  </div>
                );
              })}
              <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "0.7rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{t.out.cf}</span>
                <span className="font-serif" style={{ fontSize: "1.2rem", color: r.cashflow >= 0 ? "var(--green)" : "var(--red)" }}>{(r.cashflow >= 0 ? "+$" : "-$") + Math.abs(Math.round(r.cashflow)).toLocaleString()}</span>
              </div>
            </div>
            {r.note && <p style={{ textAlign: "center", color: "var(--gold)", fontSize: "0.75rem", marginBottom: "1rem" }}>{r.note}</p>}
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-gold" style={{ marginTop: "auto", width: "100%" }}>{t.cta}</a>
            <div style={{ marginTop: "0.9rem", display: "flex", gap: "0.5rem" }}>
              <input
                className="field"
                data-testid="calc-email-input"
                type="email"
                placeholder={t.email?.placeholder || "Email me these numbers"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && emailResults()}
                style={{ flex: 1 }}
              />
              <button
                className="btn-dark"
                data-testid="calc-email-btn"
                onClick={emailResults}
                disabled={sending}
                style={{ whiteSpace: "nowrap" }}
              >
                <Mail size={14} /> {sending ? "…" : (t.email?.button || "Send")}
              </button>
            </div>
            <p style={{ textAlign: "center", color: "var(--muted-2)", fontSize: "0.72rem", marginTop: "0.8rem" }}>{t.ctaNote}</p>
          </div>
        </div>
        <p style={{ color: "var(--muted-2)", fontSize: "0.7rem", marginTop: "2rem" }}>{t.disclaimer}</p>
      </div>
    </section>
  );
}
